import React, { useState, useEffect } from "react";
import axios from "axios";
import { Input, Button, Form, message, Spin } from "antd";
import "./configSettingsV2.css";

const ConfigSettingsV2 = () => {
  const apiUrl = process.env.REACT_APP_ConfigSetting;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bonusLimit, setBonusLimit] = useState("");

  const fetchConfigSetting = async () => {
    try {
      setLoading(true);
      const response = await axios.get(apiUrl);
      let configSetting = response.data?.body;
      if (typeof configSetting === "string") {
        try {
          configSetting = JSON.parse(configSetting);
        } catch (e) {
          configSetting = {};
        }
      }
      configSetting = configSetting || {};
      setBonusLimit(configSetting.bonus_limit ?? "");
      form.setFieldsValue({
        currency_rate: configSetting.currency_rate ?? "",
        numbers_count: configSetting.numbers_count ?? "",
        special_tester_sim_limit: configSetting.special_tester_sim_limit ?? "",
        otp_limit_tester: configSetting.otp_limit_tester ?? "",
        otp_limit_special_tester: configSetting.otp_limit_special_tester ?? "",
        sim_limit_batch2: configSetting.sim_limit_batch2 ?? "",
        sim_limit_batch3: configSetting.sim_limit_batch3 ?? "",
        otp_limit_batch2: configSetting.otp_limit_batch2 ?? "",
        otp_limit_batch3: configSetting.otp_limit_batch3 ?? "",
        payout_lower_limit: configSetting.payout_lower_limit ?? "",
      });
    } catch (error) {
      console.error("Error fetching config setting:", error);
      message.error("Failed to load configuration");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigSetting();
  }, []);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      await axios.put(apiUrl, {
        currency_rate: values.currency_rate,
        bonus_limit: bonusLimit,
        payout_lower_limit: values.payout_lower_limit,
        numbers_count: values.numbers_count,
        st_sim_limit: values.special_tester_sim_limit,
        otp_limit_special_tester: values.otp_limit_special_tester,
        otp_limit_tester: values.otp_limit_tester,
        sim_limit_batch2: values.sim_limit_batch2,
        sim_limit_batch3: values.sim_limit_batch3,
        otp_limit_batch2: values.otp_limit_batch2,
        otp_limit_batch3: values.otp_limit_batch3,
      });
      message.success("Settings updated successfully");
    } catch (error) {
      console.error("Error updating settings:", error);
      message.error("Failed to update settings");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="config-settings-v2">
      <div className="config-settings-v2-header">
        <h1 className="config-settings-v2-title">Configuration Settings</h1>
      </div>

      <div className="config-settings-v2-card">
        {loading ? (
          <div className="config-settings-v2-loading">
            <Spin size="large" />
          </div>
        ) : (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="config-settings-v2-form"
        >
          <div className="config-settings-v2-section">
            <h3 className="config-settings-v2-section-title">Rates & Limits</h3>
            <div className="config-settings-v2-grid">
              <Form.Item name="currency_rate" label="Currency Rate">
                <Input placeholder="Currency rate" className="config-settings-v2-input" />
              </Form.Item>
              <Form.Item name="payout_lower_limit" label="Payout Lower Limit">
                <Input placeholder="Payout lower limit" className="config-settings-v2-input" />
              </Form.Item>
            </div>
          </div>

          <div className="config-settings-v2-section">
            <h3 className="config-settings-v2-section-title">SIM Limits (Manual Testing)</h3>
            <div className="config-settings-v2-grid">
              <Form.Item
                name="numbers_count"
                label="SIM limit per operator, per day"
              >
                <Input
                  placeholder="SIM limit per operator (Manual Testing), per day"
                  className="config-settings-v2-input"
                />
              </Form.Item>
            </div>
          </div>

          <div className="config-settings-v2-section">
            <h3 className="config-settings-v2-section-title">SIM Limits (Special Testing)</h3>
            <div className="config-settings-v2-grid">
              <Form.Item
                name="special_tester_sim_limit"
                label="SIM limit per operator, per day"
              >
                <Input
                  placeholder="SIM limit per operator (Special Testing), per day"
                  className="config-settings-v2-input"
                />
              </Form.Item>
              <Form.Item name="sim_limit_batch2" label="Additional SIM limit for batch 2">
                <Input placeholder="Additional SIM limit for batch 2" className="config-settings-v2-input" />
              </Form.Item>
              <Form.Item name="sim_limit_batch3" label="Additional SIM limit for batch 3">
                <Input placeholder="Additional SIM limit for batch 3" className="config-settings-v2-input" />
              </Form.Item>
            </div>
          </div>

          <div className="config-settings-v2-section">
            <h3 className="config-settings-v2-section-title">OTP Limits</h3>
            <div className="config-settings-v2-grid">
              <Form.Item
                name="otp_limit_tester"
                label="Minimum OTPs (normal tester) to avoid suspension"
              >
                <Input
                  placeholder="Minimum OTPs to be generated by normal tester"
                  className="config-settings-v2-input"
                />
              </Form.Item>
              <Form.Item
                name="otp_limit_special_tester"
                label="Minimum OTPs (special tester) to avoid suspension"
              >
                <Input
                  placeholder="Minimum OTPs to be generated by special tester"
                  className="config-settings-v2-input"
                />
              </Form.Item>
              <Form.Item name="otp_limit_batch2" label="OTP limit to lock batch 2 of SIMs">
                <Input placeholder="OTP limit to lock batch 2" className="config-settings-v2-input" />
              </Form.Item>
              <Form.Item name="otp_limit_batch3" label="OTP limit to lock batch 3 of SIMs">
                <Input placeholder="OTP limit to lock batch 3" className="config-settings-v2-input" />
              </Form.Item>
            </div>
          </div>

          <Form.Item className="config-settings-v2-submit-wrap">
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              className="config-settings-v2-submit-btn"
            >
              Save Settings
            </Button>
          </Form.Item>
        </Form>
        )}
      </div>
    </div>
  );
};

export default ConfigSettingsV2;
